import { IonButton, IonContent, IonHeader, IonIcon, IonItem, IonLabel, IonList, IonPage, IonTitle, IonToolbar } from '@ionic/react';
import { IonButtons } from '@ionic/react';
import { checkmarkOutline, play } from 'ionicons/icons';
import { useState } from 'react';
import styles from '../DoorPolicyModal.module.css';
import { useCommonTranslation, useModalTranslation } from '../../hooks/useTranslation';

export type SoundOption = {
    id: string;
    labelKey: string;
    url?: string;
};

export const soundOptions: SoundOption[] = [
    { id: 'success', labelKey: 'door_policy.sounds.success', url: 'https://s3.eu-west-1.amazonaws.com/media.onlycat.com/romfs/sound/success.flac' },
    { id: 'fanfare', labelKey: 'door_policy.sounds.fanfare', url: 'https://s3.eu-west-1.amazonaws.com/media.onlycat.com/romfs/sound/fanfare.flac' },
    { id: 'deny', labelKey: 'door_policy.sounds.deny', url: 'https://s3.eu-west-1.amazonaws.com/media.onlycat.com/romfs/sound/deny.flac' },
    { id: 'coin', labelKey: 'door_policy.sounds.coin', url: 'https://s3.eu-west-1.amazonaws.com/media.onlycat.com/romfs/sound/coin.flac' },
    { id: 'choir', labelKey: 'door_policy.sounds.choir', url: 'https://s3.eu-west-1.amazonaws.com/media.onlycat.com/romfs/sound/choir.flac' },
    { id: 'bell', labelKey: 'door_policy.sounds.bell', url: 'https://s3.eu-west-1.amazonaws.com/media.onlycat.com/romfs/sound/bell.flac' },
    { id: 'angry-meow', labelKey: 'door_policy.sounds.angry_meow', url: 'https://s3.eu-west-1.amazonaws.com/media.onlycat.com/romfs/sound/angry-meow.flac' },
    { id: 'alarm', labelKey: 'door_policy.sounds.alarm', url: 'https://s3.eu-west-1.amazonaws.com/media.onlycat.com/romfs/sound/alarm.flac' },
    { id: 'affirm', labelKey: 'door_policy.sounds.affirm', url: 'https://s3.eu-west-1.amazonaws.com/media.onlycat.com/romfs/sound/affirm.flac' },
];

export const getSoundLabel = (t: (key: string, options?: Record<string, unknown>) => string, soundId: string) => {
    if (!soundId) {
        return t('door_policy.pickers.sound.no_sound');
    }
    const match = soundOptions.find((sound) => sound.id === soundId);
    return match ? t(match.labelKey) : soundId;
};

export interface EditDoorPolicySoundPickerProps {
    soundInput: string;
    onSelectSound: (soundId: string) => void;
    onClose: () => void;
}

const EditDoorPolicySoundPicker = ({
    soundInput,
    onSelectSound,
    onClose,
}: EditDoorPolicySoundPickerProps) => {
    const [selectedSound, setSelectedSound] = useState(soundInput);
    const { t: tCommon } = useCommonTranslation();
    const { t: tModals } = useModalTranslation();
    const playPreview = (url?: string) => {
        if (!url) {
            return;
        }
        const audio = new Audio(url);
        audio.play().catch(() => undefined);
    };

    const soundPickerOptions: Array<{ id: string; label: string; url?: string }> = [
        { id: '', label: tModals('door_policy.pickers.sound.no_sound') },
        ...soundOptions.map((sound) => ({
            ...sound,
            label: tModals(sound.labelKey),
        })),
    ];

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton color="medium" onClick={onClose}>
                            {tCommon('buttons.cancel')}
                        </IonButton>
                    </IonButtons>
                    {/* ios title mode keeps the title centered in md too */}
                    <IonTitle {...({ mode: 'ios' } as object)}>{tModals('door_policy.pickers.sound.title')}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton
                            strong={true}
                            onClick={() => {
                                onSelectSound(selectedSound);
                                onClose();
                            }}
                        >
                            {tCommon('buttons.save')}
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen color="light">
                <section className={`${styles.wrapper} ${styles.section}`}>
                    <h2 className={styles.sectionLabel}>{tModals('door_policy.pickers.sound.heading')}</h2>
                    <IonList className={styles.listCard}>
                        {soundPickerOptions.map((sound) => (
                            <IonItem
                                key={sound.id || 'none'}
                                button
                                detail={false}
                                lines="full"
                                onClick={() => setSelectedSound(sound.id)}
                            >
                                {sound.url && (
                                    <IonButton
                                        slot="start"
                                        fill="clear"
                                        color="primary"
                                        aria-label={tModals('door_policy.pickers.sound.preview', { sound: sound.label })}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            playPreview(sound.url);
                                        }}
                                    >
                                        <IonIcon icon={play} />
                                    </IonButton>
                                )}
                                {selectedSound === sound.id && (
                                    <IonIcon
                                        slot="end"
                                        color="primary"
                                        icon={checkmarkOutline}
                                    />
                                )}
                                <IonLabel>{sound.label}</IonLabel>

                            </IonItem>
                        ))}
                    </IonList>
                </section>
            </IonContent>
        </IonPage>
    );
};

export const EditDoorPolicySoundPickerView = (props: EditDoorPolicySoundPickerProps) => (
    <EditDoorPolicySoundPicker {...props} />
);

export default EditDoorPolicySoundPicker;
